import React, { useState, useEffect } from 'react';
import AddressInput from '../../components/common/AddressInput';
import { carrierApi } from '../../firebase/functionsService';
import { useCarrierCustomers } from '../../hooks/useCarrierCustomers';
import { useCarrierPickups } from '../../hooks/useCarrierPickups';
import { activeMassTypes, massLabel, useMassCatalog } from '../../utils/massCatalog';
import {
  EMPTY_ORDER_FORM, formFromOrder, validateOrderForm, payloadFromForm, notePricePayload, supportsPackaging, defaultMassType,
} from '../../utils/carrierOrderForm';
import Field from './Field';
import { ui } from './carrierStyles';

// Skjema for en egen ordre. order = null oppretter, ellers endres ordren.
//   mode 'full': alle felt (planned, offered, matched). mode 'noteprice': bare notat og pris (under transport).
// Velges en kunde fra registeret, tar serveren en ny kopi av kundeopplysningene ved lagring.
export default function CarrierOrderForm({ orgId, order = null, mode = 'full', onSaved, onCancel }) {
  useMassCatalog();
  const { customers, loading: customersLoading } = useCarrierCustomers(orgId);
  const { pickups } = useCarrierPickups(orgId);
  const [form, setForm] = useState(() => (order ? formFromOrder(order) : { ...EMPTY_ORDER_FORM, massType: defaultMassType() }));
  // Er kunden i registeret slettet, brukes kopien på ordren (form.customer) som manuell kunde
  useEffect(() => {
    if (customersLoading || !form.customerId) return;
    if (!customers.some(c => c.id === form.customerId)) setForm(f => ({ ...f, customerId: '' }));
  }, [customersLoading, customers, form.customerId]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setCustomer = (field) => (e) => setForm(f => ({ ...f, customer: { ...f.customer, [field]: e.target.value } }));
  const noteOnly = mode === 'noteprice';

  async function submit(e) {
    e.preventDefault();
    let found = {};
    if (noteOnly) {
      const price = String(form.agreedPrice).trim();
      if (price !== '' && !/^\d+$/.test(price)) found.agreedPrice = 'Prisen må være et heltall i kroner';
    } else {
      found = validateOrderForm(form);
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStatus('saving');
    setMessage('');
    try {
      let orderId = order?.id ?? null;
      if (noteOnly) {
        await carrierApi.updateCarrierOrder({ orgId, orderId, input: notePricePayload(form) });
      } else if (order) {
        await carrierApi.updateCarrierOrder({ orgId, orderId, input: payloadFromForm(form) });
      } else {
        orderId = (await carrierApi.createCarrierOrder({ orgId, input: payloadFromForm(form) })).orderId;
      }
      setStatus('idle');
      if (onSaved) onSaved(orderId);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Kunne ikke lagre ordren');
    }
  }

  const priceAndNote = (
    <>
      <Field id="o-price" label="Avtalt pris (kr eks. mva, valgfritt)" error={errors.agreedPrice}>
        <input id="o-price" style={ui.input} value={form.agreedPrice} onChange={set('agreedPrice')} inputMode="numeric" />
      </Field>
      <Field id="o-note" label="Notat til sjåfør">
        <textarea id="o-note" style={{ ...ui.input, height: 80, padding: 12 }} value={form.dispatcherNote} onChange={set('dispatcherNote')} maxLength={1000} />
      </Field>
    </>
  );

  const actions = (
    <>
      {status === 'error' && <p role="alert" style={ui.error}>{message}</p>}
      <div style={{ ...ui.row, marginTop: 16 }}>
        <button type="submit" className="tippi-btn" style={ui.btn} disabled={status === 'saving'}>
          {status === 'saving' ? 'Lagrer…' : (noteOnly ? 'Lagre' : 'Lagre ordre')}
        </button>
        {onCancel && <button type="button" style={ui.btnSecondary} onClick={onCancel}>Avbryt</button>}
      </div>
    </>
  );

  if (noteOnly) {
    return <form onSubmit={submit} noValidate>{priceAndNote}{actions}</form>;
  }

  return (
    <form onSubmit={submit} noValidate>
      <h2 style={ui.h2}>Kunde</h2>
      <Field id="o-customer" label="Kunde">
        <select id="o-customer" style={ui.input} value={form.customerId} onChange={set('customerId')}>
          <option value="">Skriv inn ny kunde</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      {!form.customerId && (
        <>
          <Field id="o-cname" label="Kundens navn" error={errors['customer.name']}>
            <input id="o-cname" style={ui.input} value={form.customer.name} onChange={setCustomer('name')} maxLength={120} />
          </Field>
          <div style={ui.row}>
            <Field id="o-ccontact" label="Kontaktperson" style={{ flex: 1, minWidth: 160 }}>
              <input id="o-ccontact" style={ui.input} value={form.customer.contactName} onChange={setCustomer('contactName')} maxLength={80} />
            </Field>
            <Field id="o-cphone" label="Telefon" style={{ flex: 1, minWidth: 140 }}>
              <input id="o-cphone" style={ui.input} value={form.customer.phone} onChange={setCustomer('phone')} inputMode="tel" maxLength={30} />
            </Field>
          </div>
        </>
      )}

      <h2 style={{ ...ui.h2, marginTop: 20 }}>Henting og levering</h2>
      {pickups.length > 0 && (
        <div style={{ ...ui.row, marginBottom: 8 }}>
          {pickups.map(p => (
            <button key={p.id} type="button" style={ui.btnSecondary} aria-label={`Hent fra ${p.name}`}
                    onClick={() => setForm(f => ({ ...f, pickupAddress: p.address }))}>
              {p.name}
            </button>
          ))}
        </div>
      )}
      <AddressInput label="Hentested" value={form.pickupAddress} onChange={(a) => setForm(f => ({ ...f, pickupAddress: a }))} />
      {errors.pickupAddress && <div role="alert" style={ui.fieldError}>{errors.pickupAddress}</div>}
      <div style={{ marginTop: 10 }}>
        <AddressInput label="Leveringssted" value={form.deliveryAddress} onChange={(a) => setForm(f => ({ ...f, deliveryAddress: a }))} />
        {errors.deliveryAddress && <div role="alert" style={ui.fieldError}>{errors.deliveryAddress}</div>}
      </div>

      <h2 style={{ ...ui.h2, marginTop: 20 }}>Masse</h2>
      <div style={ui.row}>
        <Field id="o-mass" label="Massetype" style={{ flex: 1, minWidth: 160 }}>
          <select id="o-mass" style={ui.input} value={form.massType} onChange={set('massType')}>
            {!activeMassTypes().some(t => t.key === form.massType) && <option value={form.massType}>{massLabel(form.massType)}</option>}
            {activeMassTypes().map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <Field id="o-tons" label="Mengde" error={errors.estimatedTons} style={{ flex: 1, minWidth: 120 }}>
          <input id="o-tons" style={ui.input} value={form.estimatedTons} onChange={set('estimatedTons')} inputMode="decimal" />
        </Field>
        <Field id="o-unit" label="Enhet" style={{ flex: '0 0 130px' }}>
          <select id="o-unit" style={ui.input} value={form.unit} onChange={set('unit')}>
            <option value="tons">Tonn</option>
            <option value="bags">Sekker</option>
          </select>
        </Field>
      </div>
      {supportsPackaging(form.massType) && (
        <Field id="o-pack" label="Pakking">
          <select id="o-pack" style={ui.input} value={form.packaging} onChange={set('packaging')}>
            <option value="bulk">Løsmasse</option>
            <option value="bagged">Sekk</option>
          </select>
        </Field>
      )}
      <Field id="o-desc" label="Beskrivelse (valgfri)">
        <input id="o-desc" style={ui.input} value={form.massDescription} onChange={set('massDescription')} maxLength={200} />
      </Field>

      <h2 style={{ ...ui.h2, marginTop: 20 }}>Tidspunkt</h2>
      <div style={ui.row}>
        <Field id="o-date" label="Dato" error={errors.date} style={{ flex: 1, minWidth: 150 }}>
          <input id="o-date" type="date" style={ui.input} value={form.date} onChange={set('date')} />
        </Field>
        <Field id="o-time" label="Klokkeslett" style={{ flex: 1, minWidth: 120 }}>
          <input id="o-time" type="time" step="900" style={ui.input} value={form.time} onChange={set('time')} />
        </Field>
      </div>

      <h2 style={{ ...ui.h2, marginTop: 20 }}>Pris og notat</h2>
      {priceAndNote}
      {actions}
    </form>
  );
}
